// ----------------------------------------------------------------------
// CmprLoader — the "[cmpr]" typing loader, per design/loader/CmprLoader.tsx.
// Brackets appear first, then each letter of `cmpr` pops in (fade + rise)
// while the brackets spread to frame it. Holds, then loops. Respects
// reduced-motion (shows the static "[cmpr]"). Requires DM Sans weight 900
// (loaded app-wide via @fontsource-variable/dm-sans in src/main.tsx).
// ----------------------------------------------------------------------
import { useRef, useState, useEffect, useLayoutEffect } from 'react';

export type CmprLoaderProps = {
  size?: number; // font-size in px (drives everything)
  word?: string; // default 'cmpr'
  speed?: number; // ms between letters
  holdFull?: number; // pause once fully typed
  holdEmpty?: number; // pause before retyping
  lead?: number; // brackets shown alone before first letter
  bracketColor?: string; // default cobalt
  textColor?: string; // default 'currentColor'
  loop?: boolean; // default true
  className?: string;
  style?: React.CSSProperties;
  'aria-label'?: string;
};

export function CmprLoader({
  size = 64,
  word = 'cmpr',
  speed = 180,
  holdFull = 1200,
  holdEmpty = 480,
  lead = 360,
  bracketColor = '#1a4fd6',
  textColor = 'currentColor',
  loop = true,
  className,
  style,
  ...rest
}: CmprLoaderProps) {
  const letters = word.split('');
  const refs = useRef<Array<HTMLSpanElement | null>>([]);
  const widths = useRef<number[]>([]);
  const [shown, setShown] = useState(0);
  const reduce =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // measure natural letter widths (scrollWidth ignores the maxWidth clamp)
  useLayoutEffect(() => {
    widths.current = refs.current.map((el) => (el ? Math.ceil(el.scrollWidth) + 1 : 0));
    setShown((s) => s); // trigger re-render with measured widths
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [word, size]);

  useEffect(() => {
    if (reduce) {
      setShown(letters.length);
      return undefined;
    }
    let t: ReturnType<typeof setTimeout>;
    let i = 0;
    let cancelled = false;
    const type = () => {
      if (cancelled) return;
      if (i < letters.length) {
        i += 1;
        setShown(i);
        t = setTimeout(type, speed);
      } else if (loop) {
        t = setTimeout(reset, holdFull);
      }
    };
    const reset = () => {
      if (cancelled) return;
      i = 0;
      setShown(0);
      t = setTimeout(type, holdEmpty);
    };
    t = setTimeout(type, lead);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speed, holdFull, holdEmpty, lead, loop, reduce, word]);

  const wrap: React.CSSProperties = {
    fontFamily: '"DM Sans Variable", "DM Sans", sans-serif',
    fontWeight: 900,
    letterSpacing: '-0.04em',
    fontSize: size,
    lineHeight: 1,
    color: textColor,
    display: 'inline-flex',
    alignItems: 'baseline',
    userSelect: 'none',
    ...style,
  };

  return (
    <span
      className={className}
      style={wrap}
      role="status"
      aria-live="polite"
      aria-label={rest['aria-label'] ?? 'Loading'}
    >
      <span aria-hidden style={{ color: bracketColor }}>
        [
      </span>
      <span aria-hidden style={{ display: 'inline-flex', overflow: 'hidden' }}>
        {letters.map((ch, i) => {
          const on = i < shown;
          return (
            <span
              key={i}
              ref={(el) => {
                refs.current[i] = el;
              }}
              style={{
                display: 'inline-block',
                overflow: 'hidden',
                verticalAlign: 'baseline',
                maxWidth: on ? widths.current[i] || 60 : 0,
                opacity: on ? 1 : 0,
                transform: on ? 'none' : 'translateY(0.16em)',
                transition:
                  'max-width .16s ease, opacity .16s ease, transform .22s cubic-bezier(.3,1.3,.5,1)',
              }}
            >
              {ch}
            </span>
          );
        })}
      </span>
      <span aria-hidden style={{ color: bracketColor }}>
        ]
      </span>
    </span>
  );
}

export default CmprLoader;
